<template>
  <div>
    <!-- 新增/编辑的对话框 -->
    <el-dialog 
      class="edit-dialog" 
      :title="title" 
      :visible.sync="dialogVisible" 
      width="70%" 
      :modal-append-to-body="true"
      append-to-body 
      close-on-click-modal 
      @closed="handleClose">
      
      <el-form 
        ref="form" 
        :model="form" 
        :rules="rules" 
        label-width="120px" 
        label-position="right">
        
        <!-- 录入方式选择 -->
        <el-form-item label="录入方式" prop="inputType" required>
          <el-radio-group v-model="form.inputType" @change="handleInputTypeChange">
            <el-radio label="manual">手动添加</el-radio>
            <el-radio label="batch">批量录入</el-radio>
          </el-radio-group>
        </el-form-item>
        
        <!-- 渠道选择 - 只在手动添加模式下显示 -->
        <el-form-item v-if="form.inputType === 'manual'" label="渠道" prop="channelId" required>
          <el-select 
            v-model="form.channelId" 
            filterable 
            :filter-method="dataFilter"
            clearable 
            placeholder="请选择渠道" 
            style="width: 100%"
            @visible-change="handleVisibleChange">
            <el-option 
              v-for="item in channelShowOptions" 
              :key="item.id" 
              :label="getChannelLabel(item)" 
              :value="String(item.id)">
            </el-option>
          </el-select>
        </el-form-item>
        
        <!-- 手动添加模式 -->
        <template v-if="form.inputType === 'manual'">
          <el-form-item label="UID" prop="uid" required>
            <el-input 
              v-model="form.uid" 
              type="textarea" 
              :rows="4" 
              placeholder="请输入UID，多个用逗号分隔"
              maxlength="999"
              show-word-limit>
            </el-input>
          </el-form-item>
        </template>
        
        <!-- 批量导入模式 -->
        <template v-if="form.inputType === 'batch'">
          <el-form-item label="导入文件" prop="importFile" required>
            <div class="import-section">
              <!-- 文件模板下载 -->
              <div class="template-download">
                <span class="label">文件模板：</span>
                <el-link 
                  type="primary" 
                  @click="downloadTemplate">
                  升级黑名单录入模板.xlsx
                </el-link>
              </div>
              
              <!-- 文件上传 -->
              <div class="file-upload">
                <span class="label">选择文件：</span>
                <el-upload
                  ref="upload"
                  :auto-upload="false"
                  :before-upload="beforeUpload"
                  :on-change="onFileChange"
                  :show-file-list="false"
                  accept=".xlsx,.xls"
                  :limit="1">
                  <el-button size="small" type="primary">选择文件</el-button>
                  <div slot="tip" class="el-upload__tip">只能上传 xlsx/xls 文件</div>
                </el-upload>
              </div>
              
              <!-- 已上传文件显示 -->
              <div v-if="form.importFile" class="uploaded-file">
                <span class="label">已上传：</span>
                <el-tag type="success" closable @close="removeFile">
                  {{ form.importFile.name }}
                </el-tag>
              </div>
            </div>
          </el-form-item>
        </template>
        
        <!-- 备注 -->
        <el-form-item label="备注">
          <el-input 
            v-model="form.remark" 
            type="textarea" 
            :rows="3" 
            placeholder="请输入备注（可选）"
            maxlength="999"
            show-word-limit>
          </el-input>
        </el-form-item>
      </el-form>
      
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="submitForm" :loading="submitLoading">确 定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { addUpgradeBlacklist, updateUpgradeBlacklist, getUpgradeBlacklistByChannelId, importUpgradeBlacklist, downloadUpgradeBlacklistTemplate } from "@/api/apps/upgradeBlacklistManage";
import { appChannelList } from "@/api/member/index.js";
import Message from "@/components/Message";
import { debounce } from 'lodash';

export default {
  name: "UpgradeBlacklistManageEdit",
  data() {
    return {
      /** 对话框是否可见 */
      dialogVisible: false,
      /** 操作类型：add-新增，edit-编辑 */
      operationType: "add",
      /** 编辑的数据ID */
      editId: null,
      /** 编辑时的原始渠道ID */
      originalChannelId: null,
      /** 提交加载状态 */
      submitLoading: false,
      /** 渠道选项 */
      channelOptions: [], // 所有渠道数据
      channelShowOptions: [], // 展示的渠道数据
      /** 临时保存的数据，用于切换录入方式时恢复 */
      tempManualData: {
        channelId: "",
        uid: ""
      },
      tempBatchData: {
        importFile: null
      },
      /** 表单数据 */
      form: {
        inputType: "manual",
        channelId: "",
        uid: "",
        importFile: null,
        remark: ""
      },
      /** 表单验证规则 */
      rules: {
        inputType: [
          { required: true, message: "请选择录入方式", trigger: "change" }
        ],
        channelId: [
          { 
            validator: (rule, value, callback) => {
              if (this.form.inputType === 'manual') {
                if (!value || !value.trim()) {
                  callback(new Error('请选择渠道'));
                }
              }
              callback();
            }, 
            trigger: "change" 
          }
        ],
        uid: [
          { 
            validator: (rule, value, callback) => {
              if (this.form.inputType === 'manual') {
                if (!value || !value.trim()) {
                  callback(new Error('请输入UID'));
                }
              }
              callback();
            }, 
            trigger: "blur" 
          }
        ],
        importFile: [
          { 
            validator: (rule, value, callback) => {
              if (this.form.inputType === 'batch') {
                if (!value) {
                  callback(new Error('请选择导入文件'));
                }
              }
              callback();
            }, 
            trigger: "change" 
          }
        ]
      }
    };
  },

  mounted() {
    // 初始化时获取渠道列表
    this.getChannelList();
  },

  computed: {
    /** 弹窗标题 */
    title() {
      return this.operationType === "add" ? "新增升级黑名单" : "编辑升级黑名单";
    }
  },

  methods: {
    /** 显示弹窗 */
    show(type, data = null) {
      this.operationType = type;
      this.dialogVisible = true;
      
      if (type === 'edit' && data && data.channelId) {
        this.editId = data.channelId;
        this.getDetail(data.channelId);
      } else {
        this.resetForm();
      }
    },
    
    /** 获取详情 */
    async getDetail(channelId) {
      try {
        const { data } = await getUpgradeBlacklistByChannelId(channelId);
        if (data.data) {
          // 确保渠道ID是字符串类型，与下拉选项的value类型一致
          this.form.channelId = String(data.data.channelId);
          this.form.uid = data.data.uidList;
          this.form.remark = data.data.remark;
          // 保存原始渠道ID，用于编辑时判断是否变更
          this.originalChannelId = String(data.data.channelId);
          
          // 确保选中的渠道在显示选项中，解决编辑时只显示ID的问题
          const selectedChannelId = String(data.data.channelId);
          const selectedChannel = this.channelOptions.find(item => String(item.id) === selectedChannelId);
          if (selectedChannel) {
            // 如果选中的渠道不在当前显示选项中，添加到显示选项
            const existsInShowOptions = this.channelShowOptions.some(item => String(item.id) === selectedChannelId);
            if (!existsInShowOptions) {
              this.channelShowOptions.unshift(selectedChannel);
            }
          }
        }
      } catch (error) {
        console.error("获取详情失败", error);
        Message.error("获取详情失败");
      }
    },
    
    /** 重置表单 */
    resetForm() {
      this.form = {
        inputType: "manual",
        channelId: "",
        uid: "",
        importFile: null,
        remark: ""
      };
      this.editId = null;
      this.originalChannelId = null;
      // 清空临时保存的数据
      this.tempManualData = {
        channelId: "",
        uid: ""
      };
      this.tempBatchData = {
        importFile: null
      };
      this.$nextTick(() => {
        this.$refs.form && this.$refs.form.clearValidate();
      });
    },
    
    /** 录入方式改变 */
    handleInputTypeChange(value) {
      if (value === 'manual') {
        // 切换到手动添加：保存批量录入的数据，恢复手动添加的数据
        this.tempBatchData.importFile = this.form.importFile;
        this.form.importFile = null;
        // 恢复之前保存的手动添加数据
        this.form.channelId = this.tempManualData.channelId;
        this.form.uid = this.tempManualData.uid;
      } else {
        // 切换到批量录入：保存手动添加的数据，恢复批量录入的数据
        this.tempManualData.channelId = this.form.channelId;
        this.tempManualData.uid = this.form.uid;
        this.form.channelId = "";
        this.form.uid = "";
        // 恢复之前保存的批量录入数据
        this.form.importFile = this.tempBatchData.importFile;
      }
      this.$nextTick(() => {
        this.$refs.form && this.$refs.form.clearValidate();
      });
    },
    
    // 获取渠道列表
    async getChannelList() {
      try {
        const { data } = await appChannelList();
        if (data.code === 200) {
          this.channelOptions = data.data || [];
          // 初始只显示前50条数据
          this.channelShowOptions = this.channelOptions.slice(0, 50);
        }
      } catch (error) {
        console.error('获取渠道列表失败:', error);
      }
    },
    // 处理下拉框显示/隐藏
    handleVisibleChange(visible) {
      if (visible) {
        // 显示时重置为前50条数据减少dom渲染
        this.channelShowOptions = this.channelOptions.slice(0, 50);
      } else {
        this.channelShowOptions = [];
      }
    },
    // 过滤渠道数据
    dataFilter: debounce(function(val) {
      if (val) {
        // 在所有数据中搜索
        this.channelShowOptions = this.channelOptions.filter(item => 
          this.getChannelLabel(item).toLowerCase().indexOf(val.trim().toLowerCase()) !== -1
        );
      } else {
        // 无搜索词时显示前50条
        this.channelShowOptions = this.channelOptions.slice(0, 50);
      }
    }, 300),
    // 获取渠道显示文本
    getChannelLabel(item) {
      return `[${item.id}] ${item.name}`;
    },

    /** 下载模板文件 */
    async downloadTemplate() {
      try {
        const response = await downloadUpgradeBlacklistTemplate();
        
        // 创建下载链接
        const blob = new Blob([response.data], { 
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = '升级黑名单录入模板.xlsx';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        Message.success('模板下载成功');
      } catch (error) {
        console.error('下载模板失败:', error);
        Message.error('下载模板失败');
      }
    },
    
    /** 上传前校验 */
    beforeUpload(file) {
      const isExcel = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || 
                      file.type === 'application/vnd.ms-excel';
      const isLt2M = file.size / 1024 / 1024 < 2;

      if (!isExcel) {
        Message.error('只能上传 Excel 文件!');
        return false;
      }
      if (!isLt2M) {
        Message.error('文件大小不能超过 2MB!');
        return false;
      }
      return true;
    },
    
    /** 文件选择 */
    onFileChange(file, fileList) {
      if (fileList.length > 0) {
        this.form.importFile = file.raw;
        Message.success('文件选择成功');
      }
    },
    
    /** 移除文件 */
    removeFile() {
      this.form.importFile = null;
    },
    
    /** 提交表单 */
    submitForm() {
      this.$refs.form.validate(async (valid) => {
        if (!valid) return;
        
        this.submitLoading = true;
        try {
          if (this.form.inputType === 'batch') {
            // 批量导入模式
            if (this.form.importFile) {
              const { data } = await importUpgradeBlacklist(this.form.importFile, this.form.remark);
              // 显示后端返回的详细导入结果信息
              Message.success(data.msg || "导入成功");
            }
          } else {
            // 手动添加模式
            const submitData = {
              channelId: this.form.channelId,
              uid: this.form.uid,
              remark: this.form.remark
            };
            
            if (this.operationType === 'add') {
              await addUpgradeBlacklist(submitData);
              Message.success("新增成功");
            } else if (this.operationType === 'edit') {
              // 编辑时，如果渠道发生变更，添加原始渠道ID
              if (this.originalChannelId && this.form.channelId !== this.originalChannelId) {
                submitData.originalChannelId = this.originalChannelId;
              }
              await updateUpgradeBlacklist(submitData);
              Message.success("编辑成功");
            }
          }
          
          this.dialogVisible = false;
          this.$emit("reload");
        } catch (error) {
          console.error("操作失败", error);
          Message.error("操作失败");
        } finally {
          this.submitLoading = false;
        }
      });
    },
    
    /** 关闭弹窗 */
    handleClose() {
      this.resetForm();
    }
  }
};
</script>

<style lang="scss" scoped>
.import-section {
  .template-download, .file-upload, .uploaded-file {
    margin-bottom: 15px;
    
    .label {
      display: inline-block;
      width: 80px;
      font-weight: 500;
    }
  }
  
  .uploaded-file {
    .el-tag {
      margin-left: 10px;
    }
  }
}

.dialog-footer {
  text-align: right;
}
</style> 